/**
 * IlemSlider — a small scroll-snap slider, written to replace Swiper.
 * ---------------------------------------------------------------------------
 * Why this exists
 *   swiper-bundle.min.js (154 KB) + swiper-bundle.min.css (18 KB) were loaded
 *   on every page of the store to serve two sliders that only ever appear on
 *   the `rebrand-pdp-26-06-25-v1` product template. This file is ~3 KB and
 *   replaces both.
 *
 * How it works
 *   Dragging/swiping is NATIVE. The track (.swiper-wrapper) is a horizontally
 *   scrollable flex container with CSS scroll-snap, so touch swipe, momentum,
 *   trackpad gestures, keyboard scrolling and screen-reader behaviour all come
 *   from the browser and cost no JavaScript. This file only adds the things
 *   the browser will not do by itself: arrow buttons, pagination bullets,
 *   programmatic slideTo(), and a slideChange event.
 *
 * Markup contract (unchanged from Swiper, so no section markup was edited)
 *   <div class="swiper">            <- or .swiper-container; the root
 *     <div class="swiper-wrapper">  <- the scrolling track
 *       <div class="swiper-slide">  <- one per slide
 *
 * API surface — deliberately mirrors the parts of Swiper that call sites used
 *   new IlemSlider(rootOrSelector, options)
 *   .realIndex            current slide index (0-based)
 *   .slideTo(i[, instant])
 *   .next() / .prev()
 *   .on('slideChange', fn)
 *
 * Options
 *   slidesPerView  number (default 1)
 *   spaceBetween   number, px (default 0)
 *   loop           boolean — wraps around instantly at the ends (default false)
 *   speed          ms for programmatic scrolls (default 400)
 *   breakpoints    { minWidth: { slidesPerView, spaceBetween } } — min-width
 *                  based and last-match-wins, same semantics as Swiper's
 *   navigation     { nextEl, prevEl } CSS selectors or elements
 *   pagination     { el, clickable, renderBullet(index, className) }
 *   on             { slideChange(slider) }
 */
(function (window, document) {
  'use strict';

  var BULLET = 'swiper-pagination-bullet';
  var BULLET_ACTIVE = 'swiper-pagination-bullet-active';

  var DEFAULTS = {
    slidesPerView: 1,
    spaceBetween: 0,
    loop: false,
    speed: 400,
    breakpoints: null,
    navigation: null,
    pagination: null,
    on: null
  };

  /* Standard ease-out. Matches the feel of Swiper's default cubic easing
   * closely enough that the change is not noticeable side by side. */
  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function assign(target) {
    for (var i = 1; i < arguments.length; i++) {
      var src = arguments[i];
      if (!src) continue;
      for (var k in src) {
        if (Object.prototype.hasOwnProperty.call(src, k)) target[k] = src[k];
      }
    }
    return target;
  }

  function IlemSlider(root, options) {
    this.root = typeof root === 'string' ? document.querySelector(root) : root;
    if (!this.root) return;

    this.wrapper = this.root.querySelector('.swiper-wrapper');
    if (!this.wrapper) return;

    this.slides = [].slice.call(this.wrapper.children);
    if (!this.slides.length) return;

    this.opts = assign({}, DEFAULTS, options);
    this.realIndex = 0;
    this._events = {};
    this._raf = null;
    this._scrollRaf = null;

    this.root.classList.add('ilem-slider');

    if (this.opts.on) {
      for (var name in this.opts.on) {
        if (Object.prototype.hasOwnProperty.call(this.opts.on, name)) {
          this.on(name, this.opts.on[name]);
        }
      }
    }

    this._layout();
    this._buildPagination();
    this._bindNavigation();
    this._bindScroll();
    this._bindResize();
    this._bindInterrupt();
    this._syncActive();
  }

  /* --- element lookup ---------------------------------------------------
   * Call sites pass plain selectors like '.swiper-button-next'. Those
   * controls are not always inside the slider root — the video carousel
   * keeps its arrows in a sibling .controller-btns element — so widen the
   * search outwards rather than forcing markup changes.
   */
  IlemSlider.prototype._resolve = function (target) {
    if (!target) return null;
    if (typeof target !== 'string') return target;
    return (
      this.root.querySelector(target) ||
      (this.root.parentElement && this.root.parentElement.querySelector(target)) ||
      document.querySelector(target)
    );
  };

  /* --- responsive sizing -------------------------------------------------
   * Swiper breakpoints are min-width and the largest match wins. Slide width
   * is composed here as a calc() string rather than done with nested CSS
   * custom-property arithmetic, which is fragile across browsers.
   */
  IlemSlider.prototype._settings = function () {
    var perView = this.opts.slidesPerView;
    var gap = this.opts.spaceBetween;
    var bps = this.opts.breakpoints;

    if (bps) {
      var widths = Object.keys(bps)
        .map(Number)
        .sort(function (a, b) { return a - b; });
      for (var i = 0; i < widths.length; i++) {
        if (window.innerWidth >= widths[i]) {
          var bp = bps[widths[i]];
          if (bp.slidesPerView != null) perView = bp.slidesPerView;
          if (bp.spaceBetween != null) gap = bp.spaceBetween;
        }
      }
    }
    return { perView: perView, gap: gap };
  };

  IlemSlider.prototype._layout = function () {
    var s = this._settings();
    var gap = s.gap || 0;
    var basis =
      s.perView === 1
        ? '100%'
        : 'calc((100% - ' + gap * (s.perView - 1) + 'px) / ' + s.perView + ')';

    this.root.style.setProperty('--ilem-gap', gap + 'px');
    this.root.style.setProperty('--ilem-slide-basis', basis);
  };

  /* --- pagination -------------------------------------------------------- */
  IlemSlider.prototype._buildPagination = function () {
    var p = this.opts.pagination;
    if (!p || !p.el) return;

    this.paginationEl = this._resolve(p.el);
    if (!this.paginationEl) return;

    var html = '';
    for (var i = 0; i < this.slides.length; i++) {
      html +=
        typeof p.renderBullet === 'function'
          ? p.renderBullet(i, BULLET)
          : '<span class="' + BULLET + '"></span>';
    }
    this.paginationEl.innerHTML = html;
    this.bullets = [].slice.call(this.paginationEl.children);

    if (p.clickable) {
      var self = this;
      this.paginationEl.addEventListener('click', function (event) {
        var index = self.bullets.indexOf(event.target);
        if (index < 0) {
          for (var i = 0; i < self.bullets.length; i++) {
            if (self.bullets[i].contains(event.target)) { index = i; break; }
          }
        }
        if (index >= 0) self.slideTo(index);
      });
    }
  };

  /* --- arrows ------------------------------------------------------------ */
  IlemSlider.prototype._bindNavigation = function () {
    var nav = this.opts.navigation;
    if (!nav) return;
    var self = this;

    var next = this._resolve(nav.nextEl);
    var prev = this._resolve(nav.prevEl);

    if (next) next.addEventListener('click', function (e) { e.preventDefault(); self.next(); });
    if (prev) prev.addEventListener('click', function (e) { e.preventDefault(); self.prev(); });

    this.nextEl = next;
    this.prevEl = prev;
  };

  /* --- index tracking ----------------------------------------------------
   * The browser owns scroll position, so index is derived from it rather
   * than stored. rAF-throttled: scroll fires far more often than paint.
   */
  IlemSlider.prototype._bindScroll = function () {
    var self = this;
    this.wrapper.addEventListener(
      'scroll',
      function () {
        if (self._scrollRaf) return;
        self._scrollRaf = window.requestAnimationFrame(function () {
          self._scrollRaf = null;
          self._syncActive();
        });
      },
      { passive: true }
    );
  };

  IlemSlider.prototype._bindResize = function () {
    var self = this;
    var timer = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        self._layout();
        self.slideTo(self.realIndex, true);
      }, 150);
    });
  };

  /* A touch or wheel gesture must win against an in-flight programmatic
   * scroll, otherwise the tween fights the user's finger. */
  IlemSlider.prototype._bindInterrupt = function () {
    var self = this;
    var cancel = function () { self._stopTween(); };
    ['pointerdown', 'touchstart', 'wheel'].forEach(function (type) {
      self.wrapper.addEventListener(type, cancel, { passive: true });
    });
  };

  IlemSlider.prototype._currentIndex = function () {
    var left = this.wrapper.scrollLeft;
    var best = 0;
    var bestDistance = Infinity;
    for (var i = 0; i < this.slides.length; i++) {
      var distance = Math.abs(this.slides[i].offsetLeft - left);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    }
    return best;
  };

  /* --- edge detection ----------------------------------------------------
   * With slidesPerView > 1 the final slides can never scroll to the left
   * edge: once the track is scrolled fully right, the last N slides sit in
   * view together. So "at the end" has to be measured from scroll position,
   * not from `index === slides.length - 1`, which is never reached.
   */
  IlemSlider.prototype._atStart = function () {
    return this.wrapper.scrollLeft <= 2;
  };

  IlemSlider.prototype._atEnd = function () {
    var el = this.wrapper;
    return el.scrollLeft >= el.scrollWidth - el.clientWidth - 2;
  };

  IlemSlider.prototype._syncActive = function () {
    var index = this._currentIndex();
    var changed = index !== this.realIndex;
    this.realIndex = index;
    this.isBeginning = this._atStart();
    this.isEnd = this._atEnd();

    if (this.bullets) {
      for (var i = 0; i < this.bullets.length; i++) {
        this.bullets[i].classList.toggle(BULLET_ACTIVE, i === index);
      }
    }

    /* Swiper's convention: arrows are only disabled when not looping. */
    if (!this.opts.loop) {
      if (this.prevEl) this.prevEl.classList.toggle('swiper-button-disabled', this.isBeginning);
      if (this.nextEl) this.nextEl.classList.toggle('swiper-button-disabled', this.isEnd);
    }

    if (changed) this._emit('slideChange');
  };

  /* --- scrolling --------------------------------------------------------- */
  IlemSlider.prototype._stopTween = function () {
    if (this._raf) {
      window.cancelAnimationFrame(this._raf);
      this._raf = null;
    }
    this.wrapper.style.scrollSnapType = '';
  };

  IlemSlider.prototype._scrollTo = function (left, instant) {
    var el = this.wrapper;
    this._stopTween();

    if (instant || !this.opts.speed) {
      el.scrollLeft = left;
      this._syncActive();
      return;
    }

    var start = el.scrollLeft;
    var delta = left - start;
    if (Math.abs(delta) < 1) return;

    /* `scroll-snap-type: x mandatory` fights a frame-by-frame scrollLeft
     * animation — the browser re-snaps mid-tween, so the movement reads as a
     * jump rather than a slide. Suspend snapping for the duration of the
     * tween and restore it afterwards, so touch swiping still snaps. This is
     * the difference between "the images slide" and "the images jump". */
    el.style.scrollSnapType = 'none';

    var duration = this.opts.speed;
    var startedAt = null;
    var self = this;

    this._raf = window.requestAnimationFrame(function step(now) {
      if (startedAt === null) startedAt = now;
      var progress = Math.min(1, (now - startedAt) / duration);
      el.scrollLeft = start + delta * easeOutCubic(progress);
      if (progress < 1) {
        self._raf = window.requestAnimationFrame(step);
      } else {
        self._raf = null;
        el.style.scrollSnapType = '';
      }
    });
  };

  /* --- public API -------------------------------------------------------- */
  IlemSlider.prototype.slideTo = function (index, instant) {
    if (!this.slides || !this.slides.length) return;
    index = Math.max(0, Math.min(index, this.slides.length - 1));
    this._scrollTo(this.slides[index].offsetLeft, instant);
  };

  /* loop:true wraps instantly rather than sliding backwards through every
   * slide, which is what a cloned-slide implementation would avoid at the
   * cost of duplicating markup. */
  IlemSlider.prototype.next = function () {
    if (this._atEnd()) {
      if (this.opts.loop) this.slideTo(0, true);
      return;
    }
    this.slideTo(this._currentIndex() + 1);
  };

  IlemSlider.prototype.prev = function () {
    if (this._atStart()) {
      if (this.opts.loop) this.slideTo(this.slides.length - 1, true);
      return;
    }
    this.slideTo(this._currentIndex() - 1);
  };

  IlemSlider.prototype.on = function (name, fn) {
    if (typeof fn !== 'function') return this;
    (this._events[name] = this._events[name] || []).push(fn);
    return this;
  };

  IlemSlider.prototype._emit = function (name) {
    var list = this._events[name];
    if (!list) return;
    for (var i = 0; i < list.length; i++) {
      try {
        list[i].call(this, this);
      } catch (err) {
        /* A listener throwing must not break index tracking for the rest. */
        if (window.console) window.console.error('IlemSlider ' + name + ' handler failed', err);
      }
    }
  };

  window.IlemSlider = IlemSlider;
})(window, document);
